import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import ProBlogCard from "./ProBlogCard";
import { getProProfile } from "../../services/proApi";
import "./Pro.css";

// =====================================================
// MY PRO PROFILE  (Task 7, Steps 5, 6, 13)
//
// The creator's own view of their Pro page.
//
// Nothing here is entered twice. The picture, name and bio are the user's
// EXISTING account profile, read back from the server; there is no separate
// "Pro profile" to fill in. The blog list is the same story — every post
// published through the Pro composer is already tagged to this creator, so the
// page simply asks for them.
//
// ── STEP 5 ──
// These blogs come from the Pro collection. They are not in the free feed, not
// in Read Blogs and not on the free author profile — that separation is what
// makes the ₹19 paywall hold.
//
// ── WHAT WAS REMOVED (Part B item 1) ──
// This page carried a six-tile stats board: Total Posts, Images, Videos,
// Views, Likes, Followers. That was the analytics the manager asked to have
// removed. It is gone, and so are the figures behind it — the server no longer
// sends totalImages, totalVideos, totalViews or totalLikes at all.
//
// What is left is what Step 6 asks for (blog count, paid user count) and what
// Step 13 asks for (Likes, Comments, Shares on each post). Nothing is tracked
// to produce those: likes and shares are stored on the post itself, and
// comments are counted from the existing comment collection.
//
// ── BLOG RENDERING (Part B items 3 and 4) ──
// Each post renders through the shared ProBlogCard, so the creator sees
// exactly what a reader sees: Title → Content → image slider → Video.
// =====================================================

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const initialOf = (name) => (name || "?").trim().charAt(0).toUpperCase();

function ProProfile() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const username = localStorage.getItem("username") || "";
  const userPic = localStorage.getItem("profilePic") || "";

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getProProfile();
      setData(res);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load your Pro profile");
    } finally {
      setLoading(false);
    }
  }, []);

  // Runs on mount and every time the page is opened again — which is the app's
  // normal behaviour, and all the "automatic content fetch" this needs.
  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-loading-screen">
          <div className="pro-spinner" />
          <p>Loading your Pro profile…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-container">
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button type="button" className="pro-btn pro-btn-primary" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { profile, membership, stats, posts } = data;

  return (
    <div className="pro-page-wrap">
      <Navbar />

      <div className="pro-container">
        {/* ── EXISTING PROFILE INFORMATION ── */}
        <section className="pro-card pro-creator-head">
          <div className="pro-avatar">
            {profile.profilePic ? (
              <img src={profile.profilePic} alt={profile.name} />
            ) : (
              <span className="pro-avatar-fallback">{initialOf(profile.name)}</span>
            )}
            <span className="pro-avatar-badge" title="Pro member">💎</span>
          </div>

          <div className="pro-creator-meta">
            <h1 className="pro-creator-name">{profile.name}</h1>
            <p className="pro-creator-tag">💎 TheBlogsSphere Pro Creator</p>
            {profile.bio && <p className="pro-creator-bio">{profile.bio}</p>}

            <div className="pro-membership-strip">
              <span className="pro-chip pro-chip-live">
                {membership.status === "active" ? "🟢 Active" : `⚪ ${membership.status}`}
              </span>
              <span className="pro-chip">
                Pro since <strong>{formatDate(membership.startDate)}</strong>
              </span>
              <span className="pro-chip">
                Valid till <strong>{formatDate(membership.expiryDate)}</strong>
              </span>
            </div>

            {/* ── STEP 6 — exactly two counters ── */}
            <div className="pro-stat-grid pro-stat-grid-2">
              <div className="pro-stat">
                <strong>{stats.blogCount}</strong>
                <small>Pro Blogs</small>
              </div>
              <div className="pro-stat">
                <strong>{stats.paidUserCount}</strong>
                <small>Paid Users</small>
              </div>
            </div>
          </div>
        </section>

        {/* ── PRO CONTENT ── */}
        <section className="pro-content-section">
          <div className="pro-section-head">
            <h2>📚 My Pro Blogs</h2>
            <button
              type="button"
              className="pro-btn pro-btn-ghost"
              onClick={() => navigate("/create-content")}
            >
              ✍️ Write Blog
            </button>
          </div>

          {posts.length === 0 ? (
            <div className="pro-card pro-empty">
              <p>
                You have not published any Pro blogs yet. Anything you publish from
                Write Blog appears here automatically.
              </p>
              <button
                type="button"
                className="pro-btn pro-btn-primary"
                onClick={() => navigate("/create-content")}
              >
                ✍️ Write your first Pro blog
              </button>
            </div>
          ) : (
            <div className="pro-post-list">
              {posts.map((post) => (
                <ProBlogCard
                  key={post._id}
                  // The creator always has full access to their own content, so
                  // these are rendered as unlocked. The server enforces the same
                  // rule — this is not the page deciding.
                  blog={{ ...post, locked: false, access: "owner", author: profile.name,
                          authorImage: profile.profilePic, likedByMe: false }}
                  compact
                  currentUsername={username}
                  currentUserPic={userPic}
                  onOpen={() => navigate(`/pro-blog/${post._id}`)}
                  // A creator may edit or delete their OWN blogs only. The
                  // server re-checks that on every save and delete.
                  onEdit={() => navigate(`/pro-blog/${post._id}/edit`)}
                />
              ))}
            </div>
          )}
        </section>

        <button
          type="button"
          className="pro-btn pro-btn-ghost pro-back"
          onClick={() => navigate("/pro-home")}
        >
          ← Pro Home
        </button>
      </div>
    </div>
  );
}

export default ProProfile;
